//@ts-check
import * as React from 'react';

import styled from '@emotion/styled';
import classnames from 'classnames';

/**
 * @typedef {object} TableProps
 * @property {string} [className]
 * @property {string} [pureCss]
 * @property {{detail: {name: {first: string}}}} [check]
 * @property {string} [containerCss]
 * @property {string} [containerClass]
 */

/**
 * @type { React.ForwardRefExoticComponent<TableProps & React.HTMLAttributes<HTMLTableElement> & React.RefAttributes<HTMLTableElement>>}
 */

const Table = React.forwardRef(
  ({ className, pureCss, containerCss, containerClass, ...props }, ref) => {
    const StyledTable = React.useMemo(
      () => styled.table`
        ${classnames(
          `
        width: 100%;
        caption-side: bottom; 
        font-size: 0.875rem; 
        line-height: 1.25rem;
        font-family: 'Open Sans', sans-serif;
        `,
          pureCss,
        )}
      `,
      [pureCss],
    );

    const ContainerTable = React.useMemo(
      () => styled.div`
        ${classnames(containerCss)}
      `,
      [containerCss],
    );

    return (
      <ContainerTable
        className={classnames(
          'position-relative w-full overflow-auto',
          containerClass,
        )}
      >
        <StyledTable
          className={classnames('', className)}
          ref={ref}
          {...props}
        />
      </ContainerTable>
    );
  },
);

Table.displayName = 'Table';

Table.defaultProps = {
  pureCss: '',
};

export default Table;

/**
 * @type { React.ForwardRefExoticComponent<TableProps & React.HTMLAttributes<HTMLTableSectionElement> & React.RefAttributes<HTMLTableSectionElement>>}
 */

const TableHeader = React.forwardRef(
  ({ className, pureCss, ...props }, ref) => {
    const StyledTableHeader = React.useMemo(
      () => styled.thead`
        ${classnames(
          `
        tr {
         
        }
        `,
          pureCss,
        )}
      `,
      [pureCss],
    );

    return (
      <StyledTableHeader
        className={classnames('', className)}
        ref={ref}
        {...props}
      />
    );
  },
);

TableHeader.displayName = 'TableHeader';

TableHeader.defaultProps = {
  pureCss: '',
};

/**
 * @type {React.ForwardRefExoticComponent<TableProps &  React.HTMLAttributes<HTMLTableSectionElement> & React.RefAttributes<HTMLTableSectionElement>>}
 */

const TableBody = React.forwardRef(({ className, pureCss, ...props }, ref) => {
  const StyledTableBody = React.useMemo(
    () => styled.tbody`
      ${classnames(`&tr:last-child{border-width: 0px;}`, pureCss)}
    `,
    [pureCss],
  );

  return (
    <StyledTableBody
      className={classnames('', className)}
      ref={ref}
      {...props}
    />
  );
});

TableBody.displayName = 'TableBody';

TableBody.defaultProps = {
  pureCss: '',
};

/**
 * @type { React.ForwardRefExoticComponent<TableProps & React.HTMLAttributes<HTMLTableSectionElement> & React.RefAttributes<HTMLTableSectionElement>>}
 */

const TableFooter = React.forwardRef(
  ({ className, pureCss, ...props }, ref) => {
    const StyledTableFooter = React.useMemo(
      () => styled.tfoot`
        ${classnames(
          `border-top-width: 1px; 
         font-weight: 500;
         & > tr { 
            border-bottom-width: 0px;
        }`,
          pureCss,
        )}
      `,
      [pureCss],
    );

    return (
      <StyledTableFooter
        className={classnames('', className)}
        ref={ref}
        {...props}
      />
    );
  },
);

TableFooter.displayName = 'TableFooter';

TableFooter.defaultProps = {
  pureCss: '',
};

/**
 * @type {React.ForwardRefExoticComponent<TableProps & React.HTMLAttributes<HTMLTableRowElement> & React.RefAttributes<HTMLTableRowElement>>}
 */

const TableRow = React.forwardRef(({ className, pureCss, ...props }, ref) => {
  const StyledTableRow = React.useMemo(
    () => styled.tr`
      ${classnames(
        `
       border-bottom-width: 1px;
     `,
        pureCss,
      )}
    `,
    [pureCss],
  );

  return (
    <StyledTableRow
      className={classnames('', className)}
      ref={ref}
      {...props}
    />
  );
});

TableRow.displayName = 'TableRow';

TableRow.defaultProps = {
  pureCss: '',
};

/**
 * @type {React.ForwardRefExoticComponent<TableProps & React.ThHTMLAttributes<HTMLTableCellElement> & React.RefAttributes<HTMLTableCellElement>>}
 */

const TableHead = React.forwardRef(({ className, pureCss, ...props }, ref) => {
  const StyledTableHead = React.useMemo(
    () => styled.th`
      ${classnames(
        `
         height: 2.5rem;
         padding-left: 0.5rem;
         padding-right: 0.5rem;
         text-align: left;
         vertical-align: middle;
         font-weight: 500;
       `,
        pureCss,
      )}
    `,
    [pureCss],
  );

  return (
    <StyledTableHead
      className={classnames('', className)}
      ref={ref}
      {...props}
    />
  );
});

TableHead.displayName = 'TableHead';

TableHead.defaultProps = {
  pureCss: '',
};

/**
 * @type {React.ForwardRefExoticComponent<TableProps & React.TdHTMLAttributes<HTMLTableCellElement> & React.RefAttributes<HTMLTableCellElement>>}
 */

const TableCell = React.forwardRef(({ className, pureCss, ...props }, ref) => {
  const StyledTableCell = React.useMemo(
    () => styled.td`
      ${classnames(
        `
        padding: 0.5rem;
        vertical-align: middle;
        
         `,
        pureCss,
      )}
    `,
    [pureCss],
  );

  return (
    <StyledTableCell
      className={classnames('react-td', className)}
      ref={ref}
      {...props}
    />
  );
});

TableCell.displayName = 'TableCell';

TableCell.defaultProps = {
  pureCss: '',
};

/**
 * @type {React.ForwardRefExoticComponent<TableProps & React.HTMLAttributes<HTMLTableCaptionElement>  >}
 */

const TableCaption = React.forwardRef(
  ({ className, pureCss, ...props }, ref) => {
    const StyledTableCaption = React.useMemo(
      () => styled.td`
        ${classnames(
          `
        margin-top: 1rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
           `,
          pureCss,
        )}
      `,
      [pureCss],
    );

    return (
      <StyledTableCaption
        className={classnames('', className)}
        ref={ref}
        {...props}
      />
    );
  },
);

TableCaption.displayName = 'TableCaption';

TableCaption.defaultProps = {
  pureCss: '',
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
